import { computed, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { filter, firstValueFrom, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { CookieService } from './cookie.service';
import { ModalName, ModalService } from './modal.service';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { ChannelName } from '../models/account/channel-name.enum';
import { ServiceName } from '../models/account/service-name.enum';
import { CustomerSubscription } from '../models/account/customer-subscription.model';
import { SubscriptionsService } from '../models/account/subscriptions-service.model';
import { SmsOfferCoupon } from '../models/sms-offer-coupon';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class SmsModalServie {
  cookieService = inject(CookieService);
  modalService = inject(ModalService);
  authService = inject(AuthService);
  account = computed(() => this.authService.state().account);
  configService =  inject(ConfigService);
  storeConfig = computed(() => this.configService.configState().store);
  httpClient = inject(HttpClient);
  private injector = inject(Injector);

  async initSmsModal() {
    if (window.location.href.includes('/printReceipt')) {
      return;
    }

    await runInInjectionContext(this.injector, async () => {
      await firstValueFrom(
        toObservable(this.storeConfig).pipe(
          filter(config => !!config)
        )
      );

      if (this.shouldOpenModal()) {
        this.fetchCoupon().subscribe((res) => {
          this.cookieService.setSmsAcknowledgementCookie(
            this.storeConfig()?.daysAfterSmsOfferSubsPopupAppears
          );
          this.modalService.openModal(ModalName.SMSOffer, { coupon: res });
        });
      }
    });
  }

  fetchCoupon(): Observable<SmsOfferCoupon> {
    //here will make the http call to get the coupon info
    return this.httpClient.get('/ws/integrated/v1/ordering/config/smsOffersCoupon');
  }

  /**
   * This method is resposible desiding if the modal should open or not.
   * Any cases that affect this should be added here
   *
   * case 1 : User has the cookie set on his browser
   * case 2 : User is already subscribed.
   *
   * @returns if we should show the modal or not.
   */
  private shouldOpenModal(): boolean {
    // Cookie Guard
    const cookie_value = this.cookieService.getSmsAcknowledgementCookie();
    if (cookie_value === '1') {
      return false;
    }

    if (!this.storeConfig()?.hasSmsOffersCoupon) {
      return false;
    }

    // User Has already subscribed
    if (!this.account()) return true;
    for (const sub of this.account()!.subscriptions) {
      if (
        sub.channelName === ChannelName.SMS &&
        sub.services?.findIndex(subService => subService.serviceName == ServiceName.OFFERS && subService.subscribed) !== -1
      ) {
        return false;
      }
    }

    return true;
  }

  addSmsChannel(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const subsciptionToAdd = new CustomerSubscription();
    subsciptionToAdd.channelName = ChannelName.SMS;
    subscriptions.push(subsciptionToAdd);
    return subscriptions;
  }

  addOffersService(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    //Preper the service
    const serviceToAdd = new SubscriptionsService();
    serviceToAdd.serviceName = ServiceName.OFFERS;
    serviceToAdd.subscribed = true;

    //Add the service to the channel
    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i] && subscriptions[i].channelName === ChannelName.SMS) {
        const offersIndex = subscriptions[i].services?.findIndex(service => service.serviceName == ServiceName.OFFERS);
        if (offersIndex == -1) {
          subscriptions[i].services?.push(serviceToAdd);
          return subscriptions;
        } else if (offersIndex === undefined) {
          subscriptions[i].services = [serviceToAdd];
        } else {
          subscriptions[i].services[offersIndex].subscribed = true;
          return subscriptions;
        }
      }
    }
    return subscriptions;
  }

  addEmailOffers(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const emailChannelExists = subscriptions.find(channel => channel.channelName === ChannelName.EMAIL);
    const emailOffersExists = emailChannelExists?.services.find(
      (service: SubscriptionsService) => service.serviceName === ServiceName.OFFERS
    );

    if (!emailChannelExists) {
      const serviceToAdd = new SubscriptionsService();
      serviceToAdd.serviceName = ServiceName.OFFERS;
      serviceToAdd.subscribed = true;

      const channelToAdd = new CustomerSubscription();
      channelToAdd.channelName = ChannelName.EMAIL;
      channelToAdd.services = [serviceToAdd];

      subscriptions.push(channelToAdd);
      return subscriptions;
    } else if (!emailOffersExists) {
      const updatedSubscriptions = this.addEmailOffersService(subscriptions);
      return updatedSubscriptions;
    } else {
      subscriptions.forEach(channel => {
        if (channel.channelName === ChannelName.EMAIL && channel.services) {
          channel.services.forEach(service => {
            if (service.serviceName === ServiceName.OFFERS) {
              service.subscribed = true;
            }
          });
        }
      });

      return subscriptions;
    }
  }

  
  addEmailChannel(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const emailSubscription = new CustomerSubscription();
    emailSubscription.channelName = ChannelName.EMAIL;
    emailSubscription.services = [];
    subscriptions.push(emailSubscription);
    return subscriptions;
  }

  addEmailOffersService(subscriptions: CustomerSubscription[]): CustomerSubscription[] {
    const serviceToAdd = new SubscriptionsService();
    serviceToAdd.serviceName = ServiceName.OFFERS;
    serviceToAdd.subscribed = true;

    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i].channelName === ChannelName.EMAIL) {
        if (!subscriptions[i].services) {
          subscriptions[i].services = [];
        }
        subscriptions[i].services.push(serviceToAdd);
      }
    }
    return subscriptions;
  }
}
